import { useCallback, useEffect, useState } from 'react'
import type { ExpandedOrganizationHierarchy, ExpandedOrganizationUser, Organization } from 'types/organization'
import type { TreeNodeDatum } from 'react-d3-tree'
import { calculateZoomFactor, countTreeNodes } from '../utils'

export interface TreeNode extends Organization, Omit<TreeNodeDatum, 'children'> {
  children?: TreeNode[]
  _children?: TreeNode[]
  __rd3t: any
  userCount: number
  users: ExpandedOrganizationUser[]
}

export const useTreeState = (organizationHierarchy: ExpandedOrganizationHierarchy | null) => {
  const [treeData, setTreeData] = useState<TreeNode | null>(null)
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set())
  const [zoomLevel, setZoomLevel] = useState(1)

  const convertToTreeNode = useCallback((org: ExpandedOrganizationHierarchy): TreeNode => {
    return {
      ...org.organization,
      __rd3t: undefined,
      userCount: org.users.length,
      users: org.users,
      children: org.children.map(convertToTreeNode),
    }
  }, [])

  useEffect(() => {
    if (organizationHierarchy) {
      const newTreeData = convertToTreeNode(organizationHierarchy)
      setTreeData(newTreeData)
    }
  }, [organizationHierarchy, convertToTreeNode])

  useEffect(() => {
    if (treeData) {
      const nodeCount = countTreeNodes(treeData)
      const zoomFactor = calculateZoomFactor(nodeCount)
      setZoomLevel(zoomFactor)
    }
  }, [treeData])

  const toggleNodeCollapse = useCallback((nodeId: string) => {
    setCollapsedNodes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }, [])

  return {
    treeData,
    collapsedNodes,
    zoomLevel,
    toggleNodeCollapse,
  }
}
